import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
const ExportModal = ({ isOpen, onClose, onConfirm }) => {
  const { t } = useTranslation('settings');
  const [selectedData, setSelectedData] = useState({
    notes: true,
    calendars: true,
    breaks: true,
  });

  if (!isOpen) return null;

  const toggleData = (key) => {
    setSelectedData((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleConfirm = () => {
    if (!selectedData.notes && !selectedData.calendars && !selectedData.breaks) {
      return;
    }
    onConfirm(selectedData);
  };

  const modalClassName = 'rounded-2xl max-w-md w-full mx-4 overflow-hidden border border-white/10 bg-[var(--color-bg-elevated)] shadow-[0_4px_12px_rgba(0,0,0,0.4)]';

  const headerClassName = 'flex items-center justify-between p-6 border-b border-white/10';

  const iconWrapperClass = 'w-12 h-12 rounded-xl flex items-center justify-center bg-[#2563eb]/20';

  const iconClass = 'w-6 h-6 text-[#7ab8f8]';

  const checkboxClass = 'w-5 h-5 rounded border transition-all cursor-pointer border-white/10 bg-[var(--color-bg-elevated)]/ text-[#3d8ef0] focus:ring-2 focus:ring-[#3d8ef0]/50/50';

  const cancelButtonClass = 'px-6 py-2.5 rounded-xl transition-all duration-200 font-medium bg-[var(--color-bg-elevated)]/ border border-white/10 text-[var(--color-text-secondary)] hover:text-textPrimary hover:border-borderDefault';

  const confirmButtonClass = 'px-6 py-2.5 rounded-xl transition-all duration-200 font-semibold bg-[var(--color-bg-elevated)] hover:bg-[var(--color-surface-hover)] text-[var(--color-text-primary)] border border-white/10';

  const isAnySelected = selectedData.notes || selectedData.calendars || selectedData.breaks;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 backdrop-blur-sm z-[100000] flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className={modalClassName}
            >
              {/* Header */}
              <div className={headerClassName}>
                <div className="flex items-center space-x-3">
                  <div className={iconWrapperClass}>
                    <Download className={iconClass} strokeWidth={2.5} />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--color-text-primary)]">
                    {t('exportModal.title')}
                  </h3>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1, backgroundColor: 'rgba(148, 163, 184, 0.1)' }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 rounded-lg transition-all duration-200 text-[var(--color-text-muted)] hover:text-textPrimary hover:bg-[var(--color-bg-elevated)]/"
                  aria-label={t('exportModal.close')}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-base leading-relaxed mb-6 text-[var(--color-text-secondary)]">
                  {t('exportModal.description')}
                </p>

                {/* Checkboxes */}
                <div className="space-y-4 mb-6">
                  {/* Notes */}
                  <motion.label
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center space-x-3 cursor-pointer p-3 rounded-xl transition-all hover:bg-[var(--color-bg-elevated)]/ border border-transparent hover:border-borderDefault"
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={selectedData.notes}
                        onChange={() => toggleData('notes')}
                        className={checkboxClass}
                      />
                      {selectedData.notes && (
                        <Check className="w-4 h-4 text-[#3d8ef0] absolute top-0.5 left-0.5 pointer-events-none" />
                      )}
                    </div>
                    <span className="text-base font-medium text-[var(--color-text-secondary)]">
                      {t('exportModal.notes')}
                    </span>
                  </motion.label>

                  {/* Calendars */}
                  <motion.label
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center space-x-3 cursor-pointer p-3 rounded-xl transition-all hover:bg-[var(--color-bg-elevated)]/ border border-transparent hover:border-borderDefault"
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={selectedData.calendars}
                        onChange={() => toggleData('calendars')}
                        className={checkboxClass}
                      />
                      {selectedData.calendars && (
                        <Check className="w-4 h-4 text-[#3d8ef0] absolute top-0.5 left-0.5 pointer-events-none" />
                      )}
                    </div>
                    <span className="text-base font-medium text-[var(--color-text-secondary)]">
                      {t('exportModal.calendars')}
                    </span>
                  </motion.label>

                  {/* Breaks */}
                  <motion.label
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center space-x-3 cursor-pointer p-3 rounded-xl transition-all hover:bg-[var(--color-bg-elevated)]/ border border-transparent hover:border-borderDefault"
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={selectedData.breaks}
                        onChange={() => toggleData('breaks')}
                        className={checkboxClass}
                      />
                      {selectedData.breaks && (
                        <Check className="w-4 h-4 text-[#3d8ef0] absolute top-0.5 left-0.5 pointer-events-none" />
                      )}
                    </div>
                    <span className="text-base font-medium text-[var(--color-text-secondary)]">
                      {t('exportModal.breaks')}
                    </span>
                  </motion.label>
                </div>

                {/* Warning */}
                {!isAnySelected && (
                  <div className="mb-6 p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                    <p className="text-sm text-orange-300 leading-relaxed">
                      {t('exportModal.selectAtLeastOne')}
                    </p>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex items-center justify-end space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02, backgroundColor: 'rgba(148, 163, 184, 0.1)' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className={cancelButtonClass}
                  >
                    {t('exportModal.cancel')}
                  </motion.button>
                  <motion.button
                    whileHover={isAnySelected ? { scale: 1.02, boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.4)' } : {}}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleConfirm}
                    disabled={!isAnySelected}
                    className={`${confirmButtonClass} ${!isAnySelected ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {t('exportModal.export')}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ExportModal;
